import React, { useState, useMemo } from 'react';
import ShowcasePageWrapper from '../../showcase/shared/ShowcasePageWrapper';
import { SLDS_CHART_COLORS } from '../../showcase/shared/ChartComponents';
import Button from '@salesforce/design-system-react/components/button';
import Icon from '@salesforce/design-system-react/components/icon';
import DataTable from '@salesforce/design-system-react/components/data-table';
import DataTableColumn from '@salesforce/design-system-react/components/data-table/column';
import DataTableCell from '@salesforce/design-system-react/components/data-table/cell';
import ExpandableSection from '@salesforce/design-system-react/components/expandable-section';
import Tabs from '@salesforce/design-system-react/components/tabs';
import TabsPanel from '@salesforce/design-system-react/components/tabs/panel';

// ---------------------------------------------------------------------------
// Style constants
// ---------------------------------------------------------------------------
const COLORS = {
  brand: '#0176d3',
  success: '#2e844a',
  warning: '#fe9339',
  error: '#c23934',
  border: '#dddbda',
  bg: '#f3f3f3',
  textPrimary: '#080707',
  textSecondary: '#706e6b',
  white: '#ffffff',
  purple: '#9050e9',
  teal: '#06a59a',
};

const cardStyle = {
  backgroundColor: COLORS.white,
  border: `1px solid ${COLORS.border}`,
  borderRadius: '4px',
  padding: '20px',
  marginBottom: '16px',
};

const fieldLabelStyle = {
  fontSize: '12px',
  color: COLORS.textSecondary,
  fontWeight: '400',
  marginBottom: '4px',
  textTransform: 'uppercase',
  letterSpacing: '0.5px',
};

const fieldValueStyle = {
  fontSize: '14px',
  color: COLORS.textPrimary,
  fontWeight: '400',
  marginBottom: '16px',
};

const Badge = ({ label, color, bgColor }) => (
  <span style={{
    display: 'inline-block', backgroundColor: bgColor, color: color,
    padding: '2px 10px', borderRadius: '12px', fontSize: '12px', fontWeight: '600', lineHeight: '20px',
  }}>
    {label}
  </span>
);

const FieldDisplay = ({ label, value, isLink }) => (
  <div style={{ marginBottom: '16px' }}>
    <div style={fieldLabelStyle}>{label}</div>
    <div style={{
      ...fieldValueStyle,
      color: isLink ? COLORS.brand : COLORS.textPrimary,
      cursor: isLink ? 'pointer' : 'default',
    }}>
      {value}
    </div>
  </div>
);

const QuickActionButton = ({ icon, label }) => (
  <button style={{
    display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '8px',
    padding: '12px 8px', backgroundColor: 'transparent', border: `1px solid ${COLORS.border}`,
    borderRadius: '4px', cursor: 'pointer', width: '100%',
  }}>
    <Icon category="utility" name={icon} size="small" style={{ fill: COLORS.brand }} />
    <span style={{ fontSize: '11px', color: '#3e3e3c', fontWeight: '500', textAlign: 'center', lineHeight: '1.3' }}>{label}</span>
  </button>
);

// ---------------------------------------------------------------------------
// Stage Path Component
// ---------------------------------------------------------------------------
const StagePath = ({ stages, currentStage }) => {
  const currentIndex = stages.indexOf(currentStage);
  return (
    <div style={{ display: 'flex', alignItems: 'center', gap: '0', marginTop: '12px' }}>
      {stages.map((stage, idx) => {
        const isComplete = idx < currentIndex;
        const isCurrent = idx === currentIndex;
        const bgColor = isComplete ? COLORS.success : isCurrent ? COLORS.brand : '#dddbda';
        const textColor = isComplete || isCurrent ? 'white' : '#706e6b';
        return (
          <div key={stage} style={{ display: 'flex', alignItems: 'center', flex: 1 }}>
            <div style={{
              flex: 1, backgroundColor: bgColor, color: textColor,
              padding: '8px 16px', fontSize: '12px', fontWeight: '600', textAlign: 'center',
              borderRadius: idx === 0 ? '4px 0 0 4px' : idx === stages.length - 1 ? '0 4px 4px 0' : '0',
              borderRight: idx < stages.length - 1 ? '2px solid white' : 'none',
              position: 'relative',
            }}>
              {isComplete && <Icon category="utility" name="check" size="xx-small" style={{ fill: 'white', marginRight: '4px' }} />}
              {stage}
              {isCurrent && (
                <div style={{
                  position: 'absolute', bottom: '-6px', left: '50%', transform: 'translateX(-50%)',
                  width: '0', height: '0', borderLeft: '6px solid transparent',
                  borderRight: '6px solid transparent', borderTop: `6px solid ${bgColor}`,
                }} />
              )}
            </div>
          </div>
        );
      })}
    </div>
  );
};

// ---------------------------------------------------------------------------
// Navigation Bar
// ---------------------------------------------------------------------------
const NavBar = ({ activeTab, onTabChange }) => {
  const tabs = ['Record Home', 'Details', 'Related', 'Activity'];
  return (
    <div style={{
      backgroundColor: 'white', border: `1px solid ${COLORS.border}`, borderRadius: '4px',
      marginBottom: '16px', display: 'flex', alignItems: 'center',
    }}>
      {tabs.map((tab) => (
        <button key={tab} onClick={() => onTabChange(tab)} style={{
          padding: '12px 24px', fontSize: '14px',
          fontWeight: activeTab === tab ? '700' : '400',
          color: activeTab === tab ? COLORS.brand : '#3e3e3c',
          backgroundColor: 'transparent', border: 'none',
          borderBottom: activeTab === tab ? `3px solid ${COLORS.brand}` : '3px solid transparent',
          cursor: 'pointer',
        }}>
          {tab}
        </button>
      ))}
    </div>
  );
};

// ---------------------------------------------------------------------------
// Custom DataTable Cells
// ---------------------------------------------------------------------------
const PaymentStatusCell = ({ children, ...props }) => {
  const status = props.item && props.item.status;
  const statusColors = {
    'Paid': { bg: '#e8f5e9', color: '#2e844a', border: '#a8d8b0' },
    'Scheduled': { bg: '#eef4ff', color: '#0176d3', border: '#b9d6f7' },
  };
  const colorSet = statusColors[status] || { bg: '#f3f3f3', color: '#706e6b', border: '#dddbda' };
  return (
    <DataTableCell {...props}>
      <span style={{
        display: 'inline-block', padding: '3px 10px', borderRadius: '12px', fontSize: '12px',
        fontWeight: '600', backgroundColor: colorSet.bg, color: colorSet.color, border: `1px solid ${colorSet.border}`,
      }}>
        {status}
      </span>
    </DataTableCell>
  );
};
PaymentStatusCell.displayName = DataTableCell.displayName;

const PaymentAmountCell = ({ children, ...props }) => {
  const amount = props.item && props.item.amount;
  return (
    <DataTableCell {...props}>
      <span style={{ fontWeight: '600', fontVariantNumeric: 'tabular-nums' }}>
        ${typeof amount === 'number' ? amount.toLocaleString('en-US') : amount}
      </span>
    </DataTableCell>
  );
};
PaymentAmountCell.displayName = DataTableCell.displayName;

const RelatedNameCell = ({ children, ...props }) => (
  <DataTableCell {...props}>
    <span style={{ color: COLORS.brand, cursor: 'pointer', fontWeight: '500' }}>{children}</span>
  </DataTableCell>
);
RelatedNameCell.displayName = DataTableCell.displayName;

const RelatedAmountCell = ({ children, ...props }) => {
  const amount = props.item && props.item.amount;
  return (
    <DataTableCell {...props}>
      <span style={{ fontWeight: '600', fontVariantNumeric: 'tabular-nums' }}>
        ${typeof amount === 'number' ? amount.toLocaleString('en-US') : amount}
      </span>
    </DataTableCell>
  );
};
RelatedAmountCell.displayName = DataTableCell.displayName;

// ---------------------------------------------------------------------------
// Main Component
// ---------------------------------------------------------------------------
const NPSPOpportunityMajor = () => {
  const [activeNav, setActiveNav] = useState('Record Home');
  const [relatedTab, setRelatedTab] = useState(0);

  // Acknowledgment steps
  const ackSteps = useMemo(() => [
    { label: 'Gift Received', date: 'Dec 28, 2025', complete: true },
    { label: 'Tax Receipt Sent', date: 'Dec 29, 2025', complete: true },
    { label: 'Acknowledgment Letter', date: 'Dec 30, 2025', complete: true },
    { label: 'Thank You Call', date: 'Scheduled Jan 3', complete: false },
    { label: 'Impact Report', date: 'Scheduled Feb 15', complete: false },
  ], []);

  // GAU Allocations
  const gauAllocations = useMemo(() => [
    { id: 'gau-1', name: 'Capital Campaign Fund', amount: 15000, percent: 60, color: SLDS_CHART_COLORS[0] },
    { id: 'gau-2', name: 'General Operating', amount: 7500, percent: 30, color: SLDS_CHART_COLORS[1] },
    { id: 'gau-3', name: 'Reserve Fund', amount: 2500, percent: 10, color: SLDS_CHART_COLORS[2] },
  ], []);

  // Payment schedule
  const payments = useMemo(() => [
    { id: 'p-1', number: '1 of 5', dueDate: '2025-06-01', amount: 5000, paidDate: '2025-05-28', status: 'Paid' },
    { id: 'p-2', number: '2 of 5', dueDate: '2025-09-01', amount: 5000, paidDate: '2025-08-30', status: 'Paid' },
    { id: 'p-3', number: '3 of 5', dueDate: '2025-12-01', amount: 5000, paidDate: '2025-11-28', status: 'Paid' },
    { id: 'p-4', number: '4 of 5', dueDate: '2025-12-28', amount: 5000, paidDate: '2025-12-28', status: 'Paid' },
    { id: 'p-5', number: '5 of 5', dueDate: '2026-03-01', amount: 5000, paidDate: '', status: 'Scheduled' },
  ], []);

  const paidCount = payments.filter(p => p.status === 'Paid').length;
  const paymentProgress = (paidCount / payments.length) * 100;

  // Related donations
  const relatedDonations = useMemo(() => [
    { id: 'rd-1', name: 'Morrison - Fall Fundraiser', amount: 10000, date: '2025-09-15', campaign: 'Fall Fundraiser' },
    { id: 'rd-2', name: 'Morrison - Spring Gala', amount: 5000, date: '2025-06-01', campaign: 'Spring Gala 2025' },
    { id: 'rd-3', name: 'Morrison - Monthly Gift', amount: 500, date: '2025-12-15', campaign: 'Monthly Giving' },
    { id: 'rd-4', name: 'Morrison - Year-End 2024', amount: 22000, date: '2024-12-20', campaign: 'Year-End Appeal 2024' },
  ], []);

  // Soft credits
  const softCredits = useMemo(() => [
    { id: 'sc-1', name: 'Thomas Morrison', amount: 25000, role: 'Household Member' },
    { id: 'sc-2', name: 'Sarah Kim', amount: 25000, role: 'Solicitor' },
  ], []);

  return (
    <ShowcasePageWrapper
      title="Morrison Family - Year-End Major Gift 2025"
      description="Donation/opportunity record page (Major Gift)"
      icon="opportunity"
      iconCategory="standard"
    >
      {/* Record Header + Stage Path */}
      <div style={cardStyle}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '16px' }}>
            <div style={{
              width: '48px', height: '48px', borderRadius: '8px', backgroundColor: '#FCB95B',
              display: 'flex', alignItems: 'center', justifyContent: 'center',
            }}>
              <Icon category="standard" name="opportunity" size="medium" style={{ fill: 'white' }} />
            </div>
            <div>
              <div style={{ fontSize: '12px', color: COLORS.textSecondary, textTransform: 'uppercase', letterSpacing: '0.5px', marginBottom: '4px' }}>
                Donation
              </div>
              <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
                <h1 style={{ fontSize: '22px', fontWeight: '700', color: COLORS.textPrimary, margin: 0 }}>
                  Morrison Family - Year-End Major Gift 2025
                </h1>
              </div>
              <div style={{ display: 'flex', gap: '16px', marginTop: '6px', fontSize: '13px', color: COLORS.textSecondary }}>
                <span style={{ fontSize: '20px', fontWeight: '700', color: COLORS.success }}>$25,000</span>
                <span style={{ display: 'flex', alignItems: 'center' }}>Cash &middot; Wire Transfer</span>
              </div>
            </div>
          </div>
          <div style={{ display: 'flex', gap: '8px', flexShrink: 0 }}>
            <Button label="Edit" variant="neutral" />
            <Button label="Send Receipt" variant="neutral" />
            <Button label="Acknowledge" variant="brand" />
          </div>
        </div>
        <StagePath stages={['Pledged', 'Received', 'Acknowledged', 'Thanked']} currentStage="Received" />
      </div>

      {/* Navigation Bar */}
      <NavBar activeTab={activeNav} onTabChange={setActiveNav} />

      {/* Three-Column Layout */}
      <div style={{ display: 'grid', gridTemplateColumns: '25% 50% 25%', gap: '16px', alignItems: 'start' }}>

        {/* LEFT COLUMN — Highlights */}
        <div>
          <div style={cardStyle}>
            <h3 style={{ fontSize: '14px', fontWeight: '700', marginBottom: '16px', color: COLORS.textPrimary }}>Gift Summary</h3>
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '12px' }}>
              {[
                { label: 'Gift Amount', value: '$25,000', icon: 'moneybag', color: COLORS.success },
                { label: 'Payments', value: '5', icon: 'date_input', color: COLORS.brand },
                { label: 'GAU Count', value: '3', icon: 'bundle_config', color: COLORS.purple },
                { label: 'Soft Credits', value: '2', icon: 'people', color: COLORS.teal },
              ].map(m => (
                <div key={m.label} style={{ backgroundColor: COLORS.bg, borderRadius: '4px', padding: '12px', textAlign: 'center' }}>
                  <Icon category="utility" name={m.icon} size="small" style={{ fill: m.color, marginBottom: '6px' }} />
                  <div style={{ fontSize: '11px', color: COLORS.textSecondary, textTransform: 'uppercase', letterSpacing: '0.5px', marginBottom: '4px' }}>{m.label}</div>
                  <div style={{ fontSize: '20px', fontWeight: '700', color: COLORS.textPrimary }}>{m.value}</div>
                </div>
              ))}
            </div>
          </div>

          {/* Acknowledgment Status */}
          <div style={cardStyle}>
            <h3 style={{ fontSize: '14px', fontWeight: '700', marginBottom: '16px', color: COLORS.textPrimary }}>Acknowledgment</h3>
            <div style={{ display: 'flex', flexDirection: 'column', gap: '0' }}>
              {ackSteps.map((step, idx) => (
                <div key={idx} style={{ display: 'flex', gap: '12px', position: 'relative' }}>
                  {idx < ackSteps.length - 1 && (
                    <div style={{
                      position: 'absolute', left: '11px', top: '24px', bottom: '0',
                      width: '2px', backgroundColor: step.complete ? COLORS.success : COLORS.border,
                    }} />
                  )}
                  <div style={{
                    width: '24px', height: '24px', borderRadius: '50%', flexShrink: 0, zIndex: 1,
                    backgroundColor: step.complete ? COLORS.success : COLORS.white,
                    border: step.complete ? 'none' : `2px solid ${COLORS.border}`,
                    display: 'flex', alignItems: 'center', justifyContent: 'center',
                  }}>
                    {step.complete && <Icon category="utility" name="check" size="xx-small" style={{ fill: 'white' }} />}
                  </div>
                  <div style={{ flex: 1, paddingBottom: '16px' }}>
                    <div style={{ fontSize: '13px', fontWeight: step.complete ? '600' : '400', color: step.complete ? COLORS.textPrimary : COLORS.textSecondary }}>
                      {step.label}
                    </div>
                    <div style={{ fontSize: '11px', color: COLORS.textSecondary }}>{step.date}</div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>

        {/* CENTER COLUMN — Detail + Related */}
        <div>
          {/* Gift Details */}
          <div style={cardStyle}>
            <h3 style={{ fontSize: '16px', fontWeight: '700', marginBottom: '16px', color: COLORS.textPrimary }}>
              Gift Information
            </h3>
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '0 24px' }}>
              <FieldDisplay label="Opportunity Name" value="Morrison Family - Year-End Major Gift 2025" />
              <FieldDisplay label="Amount" value="$25,000.00" />
              <FieldDisplay label="Primary Contact" value="Margaret Morrison" isLink />
              <FieldDisplay label="Account" value="Morrison Household" isLink />
              <FieldDisplay label="Stage" value="Received" />
              <FieldDisplay label="Record Type" value="Major Gift" />
              <FieldDisplay label="Gift Type" value="Cash" />
              <FieldDisplay label="Payment Method" value="Wire Transfer" />
              <FieldDisplay label="Close Date" value="December 28, 2025" />
              <FieldDisplay label="Date Received" value="December 28, 2025" />
              <FieldDisplay label="Primary Campaign" value="Year-End Appeal 2025" isLink />
              <FieldDisplay label="Fiscal Year" value="FY2025" />
              <FieldDisplay label="Acknowledgment Status" value="Receipted - Not Acknowledged" />
              <FieldDisplay label="Acknowledgment Date" value="December 30, 2025" />
              <div style={{ marginBottom: '16px' }}>
                <div style={fieldLabelStyle}>Opportunity Owner</div>
                <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginTop: '4px' }}>
                  <div style={{
                    width: '24px', height: '24px', borderRadius: '50%', backgroundColor: COLORS.brand,
                    color: COLORS.white, display: 'flex', alignItems: 'center', justifyContent: 'center',
                    fontSize: '10px', fontWeight: '700',
                  }}>SK</div>
                  <span style={{ fontSize: '14px', color: COLORS.brand, cursor: 'pointer' }}>Sarah Kim</span>
                </div>
              </div>
              <FieldDisplay label="Check/Reference Number" value="WR-2025-89421" />
            </div>
          </div>

          {/* Tribute Information */}
          <div style={cardStyle}>
            <h3 style={{ fontSize: '16px', fontWeight: '700', marginBottom: '16px', color: COLORS.textPrimary }}>
              Tribute Information
            </h3>
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '0 24px' }}>
              <FieldDisplay label="Tribute Type" value="In Honor Of" />
              <FieldDisplay label="Honoree" value="David Martinez" isLink />
              <div style={{ gridColumn: '1 / -1' }}>
                <FieldDisplay label="Tribute Message" value="In honor of David Martinez for 15 years of dedicated service to the Greater Bay Area Food Bank." />
              </div>
              <FieldDisplay label="Notification Contact" value="David Martinez" isLink />
              <FieldDisplay label="Notification Sent" value="Yes - Dec 30, 2025" />
            </div>
          </div>

          {/* Related Lists */}
          <div style={cardStyle}>
            <Tabs id="opp-related-tabs" selectedIndex={relatedTab} onSelect={(index) => setRelatedTab(index)}>
              <TabsPanel label={`GAU Allocations (${gauAllocations.length})`}>
                <div style={{ marginTop: '8px' }}>
                  {/* Stacked bar */}
                  <div style={{ height: '20px', borderRadius: '4px', overflow: 'hidden', display: 'flex', marginBottom: '12px' }}>
                    {gauAllocations.map((gau) => (
                      <div key={gau.id} style={{
                        width: `${gau.percent}%`, backgroundColor: gau.color,
                        display: 'flex', alignItems: 'center', justifyContent: 'center',
                      }}>
                        <span style={{ fontSize: '10px', fontWeight: '700', color: COLORS.white }}>{gau.percent}%</span>
                      </div>
                    ))}
                  </div>
                  {gauAllocations.map((gau) => (
                    <div key={gau.id} style={{
                      display: 'flex', justifyContent: 'space-between', alignItems: 'center',
                      padding: '10px 12px', marginBottom: '6px', backgroundColor: COLORS.bg,
                      borderRadius: '4px', borderLeft: `4px solid ${gau.color}`,
                    }}>
                      <div>
                        <div style={{ fontSize: '14px', fontWeight: '500', color: COLORS.textPrimary }}>{gau.name}</div>
                        <div style={{ fontSize: '12px', color: COLORS.textSecondary }}>{gau.percent}% of total</div>
                      </div>
                      <span style={{ fontSize: '15px', fontWeight: '700', color: COLORS.textPrimary }}>${gau.amount.toLocaleString()}</span>
                    </div>
                  ))}
                </div>
              </TabsPanel>
              <TabsPanel label={`Payments (${payments.length})`}>
                <div style={{ marginTop: '8px' }}>
                  {/* Progress bar */}
                  <div style={{ marginBottom: '12px' }}>
                    <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '6px' }}>
                      <span style={{ fontSize: '13px', fontWeight: '600', color: COLORS.textPrimary }}>
                        ${(paidCount * 5000).toLocaleString()} of $25,000
                      </span>
                      <span style={{ fontSize: '13px', fontWeight: '600', color: COLORS.brand }}>{paymentProgress}%</span>
                    </div>
                    <div style={{ height: '8px', backgroundColor: '#e5e5e5', borderRadius: '4px', overflow: 'hidden' }}>
                      <div style={{ width: `${paymentProgress}%`, height: '100%', backgroundColor: COLORS.success, borderRadius: '4px' }} />
                    </div>
                  </div>
                  <DataTable items={payments} id="payments-table" fixedLayout>
                    <DataTableColumn label="Installment" property="number" width="100px" />
                    <DataTableColumn label="Due Date" property="dueDate" width="120px" />
                    <DataTableColumn label="Amount" property="amount" width="110px">
                      <PaymentAmountCell />
                    </DataTableColumn>
                    <DataTableColumn label="Paid Date" property="paidDate" width="120px" />
                    <DataTableColumn label="Status" property="status" width="110px">
                      <PaymentStatusCell />
                    </DataTableColumn>
                  </DataTable>
                </div>
              </TabsPanel>
              <TabsPanel label={`Soft Credits (${softCredits.length})`}>
                <div style={{ marginTop: '8px' }}>
                  {softCredits.map((credit) => (
                    <div key={credit.id} style={{
                      display: 'flex', justifyContent: 'space-between', alignItems: 'center',
                      padding: '12px 0', borderBottom: `1px solid ${COLORS.bg}`,
                    }}>
                      <div>
                        <div style={{ fontSize: '14px', fontWeight: '500', color: COLORS.brand, cursor: 'pointer' }}>{credit.name}</div>
                        <div style={{ fontSize: '12px', color: COLORS.textSecondary }}>{credit.role}</div>
                      </div>
                      <span style={{ fontSize: '14px', fontWeight: '700', color: COLORS.textPrimary }}>${credit.amount.toLocaleString()}</span>
                    </div>
                  ))}
                </div>
              </TabsPanel>
              <TabsPanel label={`Related Gifts (${relatedDonations.length})`}>
                <div style={{ marginTop: '8px' }}>
                  <DataTable items={relatedDonations} id="related-donations-table" fixedLayout>
                    <DataTableColumn label="Name" property="name" width="240px">
                      <RelatedNameCell />
                    </DataTableColumn>
                    <DataTableColumn label="Amount" property="amount" width="120px">
                      <RelatedAmountCell />
                    </DataTableColumn>
                    <DataTableColumn label="Date" property="date" width="120px" />
                    <DataTableColumn label="Campaign" property="campaign" width="180px" />
                  </DataTable>
                </div>
              </TabsPanel>
            </Tabs>
          </div>
        </div>

        {/* RIGHT COLUMN — Sidebar */}
        <div>
          {/* Quick Actions */}
          <div style={cardStyle}>
            <h3 style={{ fontSize: '14px', fontWeight: '700', marginBottom: '12px', color: COLORS.textPrimary }}>Quick Actions</h3>
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: '8px' }}>
              <QuickActionButton icon="email" label="Send Receipt" />
              <QuickActionButton icon="call" label="Thank You Call" />
              <QuickActionButton icon="task" label="New Task" />
              <QuickActionButton icon="note" label="Add Note" />
              <QuickActionButton icon="edit" label="Edit Gift" />
              <QuickActionButton icon="copy" label="Clone" />
            </div>
          </div>

          {/* Donor Snapshot */}
          <div style={cardStyle}>
            <h3 style={{ fontSize: '14px', fontWeight: '700', marginBottom: '12px', color: COLORS.textPrimary }}>Donor Snapshot</h3>
            <div style={{ display: 'flex', alignItems: 'center', gap: '12px', marginBottom: '12px' }}>
              <div style={{
                width: '40px', height: '40px', borderRadius: '50%', backgroundColor: '#E27B7B',
                display: 'flex', alignItems: 'center', justifyContent: 'center',
                fontSize: '14px', fontWeight: '700', color: COLORS.white,
              }}>MM</div>
              <div>
                <div style={{ fontSize: '14px', fontWeight: '600', color: COLORS.brand, cursor: 'pointer' }}>Margaret Morrison</div>
                <div style={{ fontSize: '12px', color: COLORS.textSecondary }}>Major Donor &middot; Platinum Circle</div>
              </div>
            </div>
            {[
              ['Lifetime Giving', '$125,000'],
              ['Total Gifts', '24'],
              ['Avg Gift', '$5,208'],
              ['Donor Since', 'Jan 2016'],
              ['Consecutive Years', '10'],
            ].map(([label, val]) => (
              <div key={label} style={{ display: 'flex', justifyContent: 'space-between', padding: '6px 0', borderBottom: `1px solid ${COLORS.bg}` }}>
                <span style={{ fontSize: '12px', color: COLORS.textSecondary }}>{label}</span>
                <span style={{ fontSize: '12px', fontWeight: '600', color: COLORS.textPrimary }}>{val}</span>
              </div>
            ))}
          </div>

          {/* Campaign Info */}
          <div style={cardStyle}>
            <h3 style={{ fontSize: '14px', fontWeight: '700', marginBottom: '12px', color: COLORS.textPrimary }}>Campaign Info</h3>
            <div style={{ display: 'flex', alignItems: 'center', gap: '10px', marginBottom: '12px' }}>
              <div style={{
                width: '36px', height: '36px', borderRadius: '6px', backgroundColor: '#F49756',
                display: 'flex', alignItems: 'center', justifyContent: 'center',
              }}>
                <Icon category="standard" name="campaign" size="x-small" style={{ fill: 'white' }} />
              </div>
              <div>
                <div style={{ fontSize: '14px', fontWeight: '600', color: COLORS.brand, cursor: 'pointer' }}>Year-End Appeal 2025</div>
                <div style={{ fontSize: '12px', color: COLORS.textSecondary }}>Fundraising &middot; In Progress</div>
              </div>
            </div>
            <div style={{ marginBottom: '8px' }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '4px' }}>
                <span style={{ fontSize: '12px', color: COLORS.textSecondary }}>Progress</span>
                <span style={{ fontSize: '12px', fontWeight: '600', color: COLORS.textPrimary }}>$387K / $500K</span>
              </div>
              <div style={{ height: '6px', backgroundColor: '#e5e5e5', borderRadius: '3px', overflow: 'hidden' }}>
                <div style={{ width: '77.5%', height: '100%', backgroundColor: COLORS.brand, borderRadius: '3px' }} />
              </div>
            </div>
            {[
              ['Donors', '245'],
              ['Avg Gift', '$1,582'],
              ['This Gift', 'Top 5'],
            ].map(([label, val]) => (
              <div key={label} style={{ display: 'flex', justifyContent: 'space-between', padding: '4px 0' }}>
                <span style={{ fontSize: '12px', color: COLORS.textSecondary }}>{label}</span>
                <span style={{ fontSize: '12px', fontWeight: '600', color: COLORS.textPrimary }}>{val}</span>
              </div>
            ))}
          </div>
        </div>
      </div>
    </ShowcasePageWrapper>
  );
};

export default NPSPOpportunityMajor;
